<div class="analytics-dashboard">
  <div class="row mb-4">
    <div class="col-12">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <h4>{{ 'KPI.DASHBOARD.TITLE' | translate }}</h4>
        <div class="d-flex align-items-center gap-2">
          <button class="btn btn-outline-primary btn-sm" (click)="refreshData()">
            <nb-icon icon="refresh-outline"></nb-icon>
            {{ 'KPI.DASHBOARD.REFRESH' | translate }}
          </button>
        </div>
      </div>
      <app-report-filter-bar
        class="mt-2"
        [showStoreFilter]="false"
        [showExportButton]="false"
        [disabled]="dashboardLoading || categoryLoading"
        (filterChange)="onFilterChange($event)">
      </app-report-filter-bar>
    </div>
  </div>

  <ng-container *ngIf="!dashboardLoading">
  <!-- Sales Overview Cards -->
  <div class="row mb-4">
    <div class="col-lg-3 col-md-6 mb-3">
      <nb-card class="metric-card sales-card">
        <nb-card-body>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h6 class="text-muted mb-1">{{ 'KPI.DASHBOARD.TOTAL_REVENUE' | translate }}</h6>
              <h3 class="mb-0">{{ formatCurrency(salesOverview.totalRevenue) }}</h3>
              <small *ngIf="comparison.available && comparison.revenue"
                [class]="deltaClass(comparison.revenue.deltaPct)">
                {{ formatDelta(comparison.revenue.deltaPct) }} {{ 'KPI.DASHBOARD.VS_PREVIOUS' | translate }}
              </small>
            </div>
            <div class="metric-icon bg-primary">
              <nb-icon icon="trending-up-outline"></nb-icon>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>

    <div class="col-lg-3 col-md-6 mb-3">
      <nb-card class="metric-card orders-card">
        <nb-card-body>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h6 class="text-muted mb-1">{{ 'KPI.DASHBOARD.TOTAL_ORDERS' | translate }}</h6>
              <h3 class="mb-0">{{ formatNumber(salesOverview.totalOrders) }}</h3>
              <small *ngIf="comparison.available && comparison.orderCount"
                [class]="deltaClass(comparison.orderCount.deltaPct)">
                {{ formatDelta(comparison.orderCount.deltaPct) }} {{ 'KPI.DASHBOARD.VS_PREVIOUS' | translate }}
              </small>
            </div>
            <div class="metric-icon bg-success">
              <nb-icon icon="shopping-cart-outline"></nb-icon>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>

    <div class="col-lg-3 col-md-6 mb-3">
      <nb-card class="metric-card avg-order-card">
        <nb-card-body>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h6 class="text-muted mb-1">{{ 'KPI.DASHBOARD.AVG_ORDER_VALUE' | translate }}</h6>
              <h3 class="mb-0">{{ formatCurrency(salesOverview.averageOrderValue) }}</h3>
              <small *ngIf="comparison.available && comparison.aov"
                [class]="deltaClass(comparison.aov.deltaPct)">
                {{ formatDelta(comparison.aov.deltaPct) }} {{ 'KPI.DASHBOARD.VS_PREVIOUS' | translate }}
              </small>
            </div>
            <div class="metric-icon bg-info">
              <nb-icon icon="bar-chart-outline"></nb-icon>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>

    <div class="col-lg-3 col-md-6 mb-3">
      <nb-card class="metric-card items-card">
        <nb-card-body>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h6 class="text-muted mb-1">{{ 'KPI.DASHBOARD.TOTAL_ITEMS' | translate }}</h6>
              <h3 class="mb-0">{{ formatNumber(salesOverview.totalItems) }}</h3>
            </div>
            <div class="metric-icon bg-warning">
              <nb-icon icon="cube-outline"></nb-icon>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>
  </div>

  <!-- Stock Alerts -->
  <div class="row mb-4" *ngIf="stockAlerts?.count > 0">
    <div class="col-12">
      <nb-card>
        <nb-card-header class="d-flex align-items-center gap-2">
          {{ 'KPI.DASHBOARD.STOCK_ALERTS' | translate }}
          <nb-badge status="warning" [text]="stockAlerts.count.toString()"></nb-badge>
        </nb-card-header>
        <nb-card-body class="p-0">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>{{ 'KPI.DASHBOARD.PRODUCT' | translate }}</th>
                <th class="text-end">{{ 'KPI.DASHBOARD.CURRENT_STOCK' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of visibleStockAlerts">
                <td>{{ item.name || item.productName }}</td>
                <td class="text-end">{{ stockAlertQuantity(item) }}</td>
              </tr>
            </tbody>
          </table>
          <div class="text-center py-2" *ngIf="(stockAlerts.items?.length || 0) > 5">
            <button class="btn btn-link btn-sm" (click)="showAllStockAlerts = !showAllStockAlerts">
              {{ showAllStockAlerts ? ('KPI.DASHBOARD.SHOW_LESS' | translate) : ('KPI.DASHBOARD.VIEW_ALL_ALERTS' | translate) }}
            </button>
          </div>
        </nb-card-body>
      </nb-card>
    </div>
  </div>

  <!-- Financial Overview & Inventory Summary -->
  <div class="row mb-4">
    <div class="col-lg-6 mb-3">
      <nb-card>
        <nb-card-header>
          <h5>{{ 'KPI.DASHBOARD.CURRENT_BALANCE' | translate }}</h5>
        </nb-card-header>
        <nb-card-body>
          <div class="row">
            <div class="col-6">
              <div class="balance-item">
                <span class="label">{{ 'KPI.DASHBOARD.TOTAL_REVENUE' | translate }}</span>
                <span class="value text-success">{{ formatCurrency(currentBalance.totalRevenue) }}</span>
              </div>
            </div>
            <div class="col-6">
              <div class="balance-item">
                <span class="label">{{ 'KPI.DASHBOARD.COMMISSION' | translate }}</span>
                <span class="value text-warning">-{{ formatCurrency(currentBalance.totalCommission) }}</span>
              </div>
            </div>
            <div class="col-12 mt-3">
              <hr>
              <div class="balance-item">
                <span class="label">{{ 'KPI.DASHBOARD.NET_AMOUNT' | translate }}</span>
                <span class="value h4 text-primary">{{ formatCurrency(currentBalance.netAmount) }}</span>
              </div>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>

    <div class="col-lg-6 mb-3">
      <nb-card>
        <nb-card-header>
          <h5>{{ 'KPI.DASHBOARD.INVENTORY_SUMMARY' | translate }}</h5>
        </nb-card-header>
        <nb-card-body>
          <div class="row">
            <div class="col-6">
              <div class="inventory-item">
                <span class="label">{{ 'KPI.DASHBOARD.TOTAL_PRODUCTS' | translate }}</span>
                <span class="value">{{ formatNumber(inventorySummary.totalProducts) }}</span>
              </div>
              <div class="inventory-item">
                <span class="label">{{ 'KPI.DASHBOARD.ACTIVE_PRODUCTS' | translate }}</span>
                <span class="value text-success">{{ formatNumber(inventorySummary.activeProducts) }}</span>
              </div>
            </div>
            <div class="col-6">
              <div class="inventory-item">
                <span class="label">{{ 'KPI.DASHBOARD.LOW_STOCK' | translate }}</span>
                <span class="value text-warning">{{ formatNumber(inventorySummary.lowStockProducts) }}</span>
              </div>
              <div class="inventory-item">
                <span class="label">{{ 'KPI.DASHBOARD.OUT_OF_STOCK' | translate }}</span>
                <span class="value text-danger">{{ formatNumber(inventorySummary.outOfStockProducts) }}</span>
              </div>
            </div>
            <div class="col-12 mt-3">
              <hr>
              <div class="inventory-item">
                <span class="label">{{ 'KPI.DASHBOARD.INVENTORY_VALUE' | translate }}</span>
                <span class="value h5 text-primary">{{ formatCurrency(inventorySummary.totalInventoryValue) }}</span>
              </div>
            </div>
          </div>
        </nb-card-body>
      </nb-card>
    </div>
  </div>

  <!-- Charts Row -->
  <div class="row mb-4" *ngIf="!dashboardLoading">
    <div class="col-lg-7 mb-3">
      <nb-card>
        <nb-card-header>
          <h5>{{ 'KPI.DASHBOARD.ORDER_TRENDS' | translate }}</h5>
        </nb-card-header>
        <nb-card-body>
          <div *ngIf="orderTrends.length > 0; else noTrendData" style="display: block; min-height: 280px;">
            <canvas baseChart #trendChart
              [data]="trendChartData"
              [options]="trendChartOptions"
              [type]="trendChartType">
            </canvas>
          </div>
          <ng-template #noTrendData>
            <div class="text-center py-4 text-muted">
              <nb-icon icon="trending-up-outline" class="large-icon mb-2"></nb-icon>
              <p class="mb-0">{{ 'KPI.DASHBOARD.NO_TREND_DATA' | translate }}</p>
            </div>
          </ng-template>
        </nb-card-body>
      </nb-card>
    </div>

    <div class="col-lg-5 mb-3">
      <nb-card>
        <nb-card-header>
          <h5>{{ 'KPI.DASHBOARD.TOP_BAS' | translate }}</h5>
        </nb-card-header>
        <nb-card-body>
          <div *ngIf="categoryLoading" class="text-center py-4">
            <nb-spinner size="medium"></nb-spinner>
          </div>
          <div *ngIf="!categoryLoading && baBreakdown.length > 0; else noCategoryData" style="display: block; min-height: 280px;">
            <canvas baseChart #categoryChart
              [data]="categoryChartData"
              [options]="categoryChartOptions"
              [type]="categoryChartType">
            </canvas>
          </div>
          <ng-template #noCategoryData>
            <div *ngIf="!categoryLoading" class="text-center py-4 text-muted">
              <nb-icon icon="pie-chart-outline" class="large-icon mb-2"></nb-icon>
              <p class="mb-0">{{ 'KPI.DASHBOARD.NO_CATEGORY_DATA' | translate }}</p>
            </div>
          </ng-template>
        </nb-card-body>
      </nb-card>
    </div>
  </div>

  <!-- Top Products -->
  <div class="row mb-4">
    <div class="col-12">
      <nb-card>
        <nb-card-header>
          <h5>{{ 'KPI.DASHBOARD.TOP_PRODUCTS' | translate }}</h5>
        </nb-card-header>
        <nb-card-body>
          <div *ngIf="topProducts.length > 0; else noProducts">
            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th>{{ 'KPI.DASHBOARD.PRODUCT' | translate }}</th>
                    <th>{{ 'KPI.DASHBOARD.SKU' | translate }}</th>
                    <th class="text-center">{{ 'KPI.DASHBOARD.UNITS_SOLD' | translate }}</th>
                    <th class="text-end">{{ 'KPI.DASHBOARD.REVENUE' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let product of topProducts">
                    <td><strong>{{ product.productName }}</strong></td>
                    <td><code>{{ product.productSku }}</code></td>
                    <td class="text-center">
                      <nb-badge status="info" [text]="formatNumber(product.totalSold)"></nb-badge>
                    </td>
                    <td class="text-end"><strong>{{ formatCurrency(product.totalRevenue) }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <ng-template #noProducts>
            <div class="text-center py-4">
              <nb-icon icon="cube-outline" class="large-icon text-muted mb-3"></nb-icon>
              <p class="text-muted">{{ 'KPI.DASHBOARD.NO_PRODUCTS' | translate }}</p>
            </div>
          </ng-template>
        </nb-card-body>
      </nb-card>
    </div>
  </div>
  </ng-container>

  <div *ngIf="dashboardLoading" class="text-center py-5">
    <nb-spinner size="large"></nb-spinner>
    <p class="mt-3 text-muted">{{ 'KPI.DASHBOARD.LOADING' | translate }}</p>
  </div>

  <div *ngIf="!dashboardLoading && !salesOverview.totalRevenue && !hasChartData" class="text-center py-5">
    <nb-card>
      <nb-card-body>
        <nb-icon icon="bar-chart-outline" class="large-icon text-muted mb-3"></nb-icon>
        <h4>{{ 'KPI.DASHBOARD.NO_SALES' | translate }}</h4>
        <p class="text-muted">{{ 'KPI.DASHBOARD.NO_SALES_HINT' | translate }}</p>
      </nb-card-body>
    </nb-card>
  </div>
</div>
